import { signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import {
  formControlChanges$,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  ACC_EMPLOYED_VALUE,
  ACC_OTHER_EMPLOYED_VALUE,
  ACC_SELF_EMPLOYED_VALUE,
  AccAddressType,
  AccBooleanValue,
  AccEmploymentStatusCode,
  AccOccupationCode,
  AccWorkTypeCode,
  IAccClaimEmployer,
  IAccEmploymentForm,
} from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, ISelectOption } from '@principle-theorem/shared';
import { combineLatest } from 'rxjs';
import { AccAddressFormGroup } from './acc-address.formgroup';
import { hasRequiredError, setRequiredValidation } from './form-group-helpers';

export class AccEmploymentFormGroup extends TypedFormGroup<IAccEmploymentForm> {
  canResumeNormalWork = signal<AccBooleanValue | undefined>(undefined);

  constructor() {
    super({
      occupationCode: new TypedFormControl<ISelectOption<AccOccupationCode>>(
        undefined
      ),
      inPaidEmployment: new TypedFormControl<AccBooleanValue>(
        undefined,
        Validators.required
      ),
      employmentStatusCode: new TypedFormControl<AccEmploymentStatusCode>(
        undefined
      ),
      otherEmployment: new TypedFormControl<string>(undefined),
      workTypeCode: new TypedFormControl<AccWorkTypeCode>(undefined),
      employer: new AccEmployerFormGroup(),
      workInjury: new TypedFormControl<AccBooleanValue>(
        undefined,
        Validators.required
      ),
    });

    this._setupValidationHandlers();
  }

  get employer(): AccEmployerFormGroup {
    return this.controls.employer as AccEmployerFormGroup;
  }

  // Error getters
  get occupationCodeRequiredError(): boolean {
    return hasRequiredError(this.controls.occupationCode);
  }

  get inPaidEmploymentRequiredError(): boolean {
    return hasRequiredError(this.controls.inPaidEmployment);
  }

  get employmentStatusCodeRequiredError(): boolean {
    return hasRequiredError(this.controls.employmentStatusCode);
  }

  get workInjuryRequiredError(): boolean {
    return hasRequiredError(this.controls.workInjury);
  }

  get workTypeCodeRequiredError(): boolean {
    return hasRequiredError(this.controls.workTypeCode);
  }

  get otherEmploymentRequiredError(): boolean {
    return hasRequiredError(this.controls.otherEmployment);
  }

  get displayOtherEmployment(): boolean {
    return (
      this.controls.inPaidEmployment.value === AccBooleanValue.True &&
      this.controls.employmentStatusCode.value === ACC_OTHER_EMPLOYED_VALUE
    );
  }

  get isInPaidEmployment(): boolean {
    return this.controls.inPaidEmployment.value === AccBooleanValue.True;
  }

  get isEmployedOrSelfEmployed(): boolean {
    const status = this.controls.employmentStatusCode.value;
    const isEmployed = status === ACC_EMPLOYED_VALUE;
    const isSelfEmployed = status === ACC_SELF_EMPLOYED_VALUE;
    return isEmployed || isSelfEmployed;
  }

  get displayEmployer(): boolean {
    const employmentStatus = this.controls.employmentStatusCode.value;
    const workInjury = this.controls.workInjury.value;
    const canResumeNormalWork = this.canResumeNormalWork();

    const isEmployed = employmentStatus === ACC_EMPLOYED_VALUE;
    const isWorkAccident = workInjury === AccBooleanValue.True;
    const cannotResumeWork =
      !canResumeNormalWork || canResumeNormalWork === AccBooleanValue.False;

    return isEmployed && (isWorkAccident || cannotResumeWork);
  }

  private _setupValidationHandlers(): void {
    this.controls.inPaidEmployment.valueChanges
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((value) => {
        setRequiredValidation(value, this.controls.employmentStatusCode);
        setRequiredValidation(value, this.controls.workTypeCode);
      });

    this.controls.employmentStatusCode.valueChanges
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((value) =>
        setRequiredValidation(
          value === ACC_OTHER_EMPLOYED_VALUE,
          this.controls.otherEmployment
        )
      );

    combineLatest([
      formControlChanges$(this.controls.employmentStatusCode),
      formControlChanges$(this.controls.workInjury),
      toObservable(this.canResumeNormalWork),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([employmentStatusCode, workInjury, canResumeNormalWork]) => {
        const isEmployed = employmentStatusCode === ACC_EMPLOYED_VALUE;
        const isWorkInjury = workInjury === AccBooleanValue.True;
        const canNotResume =
          !canResumeNormalWork || canResumeNormalWork === AccBooleanValue.False;

        if (isEmployed && (isWorkInjury || canNotResume)) {
          setRequiredValidation(true, this.employer.employerName);
          this.employer.address.setAllValidators();
          return;
        }

        setRequiredValidation(false, this.employer.employerName, '');
        this.employer.address.clearAllValidators();
      });
  }
}

class AccEmployerFormGroup extends TypedFormGroup<IAccClaimEmployer> {
  constructor() {
    super({
      employerName: new TypedFormControl<string>(''),
      address: new AccAddressFormGroup(AccAddressType.Postal, false),
    });
  }

  get employerName(): TypedFormControl<string> {
    return this.controls.employerName as TypedFormControl<string>;
  }

  get address(): AccAddressFormGroup {
    return this.controls.address as AccAddressFormGroup;
  }

  get employerNameRequiredError(): boolean {
    return hasRequiredError(this.controls.employerName);
  }
}
